import React, { useState, useEffect, useMemo } from 'react'
import { ModelVersion, Prediction, FeatureImportance, TrainingJob } from '../../types/ml'
import TimeSeriesPredictionChart from '../../components/charts/TimeSeriesPredictionChart'
import FeatureAttributionChart from '../../components/charts/FeatureAttributionChart'
import RMSETrendChart from '../../components/charts/RMSETrendChart'

const NeuralNetworks: React.FC = () => {
  const [selectedSystem, setSelectedSystem] = useState('INV-01')
  const [horizon, setHorizon] = useState('24h')
  const [selectedModel, setSelectedModel] = useState('lstm-v1')
  const [prediction, setPrediction] = useState<Prediction | null>(null)
  const [featureImportance, setFeatureImportance] = useState<FeatureImportance[]>([])
  const [models, setModels] = useState<ModelVersion[]>([])
  const [trainingJob, setTrainingJob] = useState<TrainingJob | null>(null)
  const [loading, setLoading] = useState(false)

  const mockPredictionData = useMemo(() => {
    const hours = horizon === '1h' ? 4 : horizon === '6h' ? 24 : 96
    return {
      timestamps: Array.from({ length: hours }, (_, i) => 
        new Date(Date.now() + i * 15 * 60000).toISOString()
      ),
      y_pred: Array.from({ length: hours }, (_, i) => 30 + i * 0.5 + Math.sin(i * 0.1) * 10),
      lower_95: Array.from({ length: hours }, (_, i) => 20 + i * 0.4 + Math.sin(i * 0.1) * 8),
      upper_95: Array.from({ length: hours }, (_, i) => 40 + i * 0.6 + Math.sin(i * 0.1) * 12),
      confidence: Array.from({ length: hours }, (_, i) => 0.85 + Math.sin(i * 0.2) * 0.1),
      model_version: selectedModel
    }
  }, [horizon, selectedModel]);

  const mockFeatureImportance = useMemo(() => [
    { feature: 'irradiance_w_m2', importance: 0.45, impact: 'positive' as const },
    { feature: 'module_temp_c', importance: 0.23, impact: 'negative' as const },
    { feature: 'hour_of_day', importance: 0.18, impact: 'positive' as const },
    { feature: 'cloud_cover_pct', importance: 0.14, impact: 'negative' as const }
  ], []);

  useEffect(() => {
    fetchModels()
    setPrediction(mockPredictionData)
    setFeatureImportance(mockFeatureImportance)
  }, [mockPredictionData, mockFeatureImportance])

  const fetchModels = async () => {
    const mockModels: ModelVersion[] = [
      {
        id: 'lstm-v1',
        name: 'LSTM Seq2Seq',
        version: 'v1.2.3',
        type: 'neural_network',
        status: 'active',
        created_at: '2025-01-07T10:30:00Z',
        metrics: { rmse: 2.34, mae: 1.87, r_squared: 0.94 },
        hyperparameters: { hidden_size: 128, layers: 2, dropout: 0.2 },
        author: 'ml-engineer'
      },
      {
        id: 'tcn-v1',
        name: 'Temporal CNN',
        version: 'v1.0.1',
        type: 'neural_network',
        status: 'inactive',
        created_at: '2025-01-05T14:20:00Z',
        metrics: { rmse: 2.67, mae: 2.12, r_squared: 0.91 },
        hyperparameters: { kernel_size: 3, dilation: [1, 2, 4, 8] },
        author: 'ml-engineer'
      }
    ]
    setModels(mockModels)
  }

  const fetchPrediction = async () => {
    setPrediction(mockPredictionData)
    setLoading(false)
  }

  const fetchFeatureImportance = async () => {
    setFeatureImportance(mockFeatureImportance)
  }

  const handleRunPrediction = async () => {
    setLoading(true)
    setTimeout(() => {
      setPrediction(mockPredictionData)
      setLoading(false)
    }, 500)
  }

  const handleTrainModel = async () => {
    const mockJob: TrainingJob = {
      id: 'job-123',
      status: 'running',
      progress: 0,
      logs: ['Starting training...', 'Loading data...'],
      started_at: new Date().toISOString()
    }
    setTrainingJob(mockJob)
    
    // Simulate training progress
    const interval = setInterval(() => {
      setTrainingJob(prev => {
        if (!prev || prev.progress >= 100) {
          clearInterval(interval)
          return prev ? { ...prev, status: 'completed', progress: 100 } : null
        }
        return {
          ...prev,
          progress: prev.progress + 10,
          logs: [...prev.logs, `Epoch ${Math.floor(prev.progress / 10) + 1} completed`]
        }
      })
    }, 1000)
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header & Controls */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Neural Networks</h1>
        <div className="flex gap-4">
          <select 
            value={selectedSystem} 
            onChange={(e) => setSelectedSystem(e.target.value)}
            className="px-4 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-600"
          >
            <option value="INV-01">INV-01</option>
            <option value="INV-02">INV-02</option>
            <option value="INV-03">INV-03</option>
          </select>
          <select 
            value={horizon} 
            onChange={(e) => setHorizon(e.target.value)}
            className="px-4 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-600"
          >
            <option value="1h">1 Hour</option>
            <option value="6h">6 Hours</option>
            <option value="24h">24 Hours</option>
          </select>
          <select 
            value={selectedModel} 
            onChange={(e) => setSelectedModel(e.target.value)}
            className="px-4 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-600"
          >
            {models.map(model => (
              <option key={model.id} value={model.id}>{model.name}</option>
            ))}
          </select>
          <button 
            onClick={handleRunPrediction}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Running...' : 'Run Prediction'}
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white/60 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-slate-200">
          <h3 className="text-sm font-semibold text-slate-600 mb-2">Next Hour Predicted</h3>
          <p className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-blue-500 bg-clip-text text-transparent">
            {prediction?.y_pred[0]?.toFixed(1) || '--'} kW
          </p>
        </div>
        <div className="bg-white/60 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-slate-200">
          <h3 className="text-sm font-semibold text-slate-600 mb-2">24h Predicted Energy</h3>
          <p className="text-3xl font-bold bg-gradient-to-r from-green-600 to-green-500 bg-clip-text text-transparent">
            {prediction ? (prediction.y_pred.reduce((sum, val) => sum + val, 0) / 4).toFixed(1) : '--'} kWh
          </p>
        </div>
        <div className="bg-white/60 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-slate-200">
          <h3 className="text-sm font-semibold text-slate-600 mb-2">Model Confidence</h3>
          <p className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-purple-500 bg-clip-text text-transparent">
            {prediction?.confidence?.[0] ? (prediction.confidence[0] * 100).toFixed(1) : '--'}%
          </p>
        </div>
        <div className="bg-white/60 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-slate-200">
          <h3 className="text-sm font-semibold text-slate-600 mb-2">Model Version</h3>
          <p className="text-3xl font-bold bg-gradient-to-r from-orange-600 to-orange-500 bg-clip-text text-transparent">
            {selectedModel}
          </p>
        </div>
      </div>

      {/* Main Prediction Chart */}
      <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-slate-200">
        <h3 className="text-xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent mb-6">
          Neural Network Predictions 🧠
        </h3>
        <TimeSeriesPredictionChart prediction={prediction} loading={loading} />
        <p className="text-sm text-slate-500 mt-2">
          {prediction ? `${prediction.y_pred.length} predictions` : 'No data'}
        </p>
      </div>

      {/* Feature Attribution & Model Health */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-slate-200">
          <h3 className="text-xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent mb-6">
            Feature Attribution (SHAP)
          </h3>
          <FeatureAttributionChart data={featureImportance} />
        </div>

        <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-slate-200">
          <h3 className="text-xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent mb-6">
            Model Health 📊
          </h3>
          <RMSETrendChart />
        </div>
      </div>

      {/* Model Versions Table */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Model Versions</h3>
          <button 
            onClick={handleTrainModel}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            Train New Model
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                  Model
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                  Version
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                  RMSE
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {models.map((model) => (
                <tr key={model.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                    {model.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                    {model.version}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                    {model.metrics.rmse.toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      model.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {model.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex gap-2">
                      {model.status !== 'active' && (
                        <button className="text-blue-600 hover:text-blue-900">Deploy</button>
                      )}
                      <button className="text-gray-600 hover:text-gray-900">Details</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Training Progress Modal */}
      {trainingJob && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold mb-4">Training Progress</h3>
              <div className="mb-4">
                <div className="flex justify-between text-sm mb-1">
                  <span>Progress</span>
                  <span>{trainingJob.progress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${trainingJob.progress}%` }}
                  ></div>
                </div>
              </div>
              <div className="bg-gray-100 dark:bg-gray-700 p-3 rounded text-sm max-h-32 overflow-y-auto">
                {trainingJob.logs.map((log, i) => (
                  <div key={i}>{log}</div>
                ))}
              </div>
              {trainingJob.status === 'completed' && (
                <button 
                  onClick={() => setTrainingJob(null)}
                  className="mt-4 w-full px-4 py-2 bg-blue-600 text-white rounded-lg"
                >
                  Close
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default NeuralNetworks